/**
 * Offer flow: spot → edit → Gmail (club mailbox) or mailto fallback.
 * @see docs/APP_UI_SPEC.md — B4, B5
 */
import Swal from 'sweetalert2';
import { promptBookingSpotAndBringer } from './adminActions.js';
import { venueHasSpotLengths } from './bookingSpot.js';
import {
  composeBookingOffer,
  generateMailtoLink,
  openBookingMailtoLink,
} from './bookingEmail.js';
import {
  fetchComedianBookingHistoryForOffer,
  persistApplicationOfferSpot,
} from './comedianBookingHistory.js';
import { recordOfferEmailSend } from './offerEmailSend.js';
import { invokeSendBookingOffer, promptEditBookingOffer } from './bookingGmailSend.js';
import {
  buildSheetSyncCandidate,
  formatSheetSyncResultNote,
  readInboxUpdateSpreadsheetPref,
  syncLineupSheetFromInbox,
} from './sheetLineupSync.js';

async function syncSheetAfterOffer(supabase, application, event, spot) {
  if (!readInboxUpdateSpreadsheetPref()) return null;
  const result = await syncLineupSheetFromInbox(supabase, {
    cand: buildSheetSyncCandidate(application, event),
    action: 'offer',
    spot,
  });
  return formatSheetSyncResultNote(result, { enabled: true });
}

async function operatorEmail(supabase) {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.user?.email || '';
  } catch {
    return '';
  }
}

/**
 * @param {object} opts
 * @param {import('@supabase/supabase-js').SupabaseClient} opts.supabase
 * @param {{ comedian_name: string, email: string }} opts.application
 * @param {object} opts.event
 * @param {{ body: string }|null} opts.emailTemplate
 * @param {number} opts.applicationId
 * @param {(spot: { spotCode: string, isBringer: boolean }) => void|Promise<void>} opts.onOffered
 * @returns {Promise<{ sent: boolean, cancelled?: boolean, channel?: 'gmail'|'mailto' }>}
 */
export async function runBookingOfferSend({
  supabase,
  application,
  event,
  emailTemplate,
  applicationId,
  onOffered,
}) {
  const bookingHistoryRows = await fetchComedianBookingHistoryForOffer(supabase, application.email, {
    excludeApplicationId: applicationId,
  });
  const spot = venueHasSpotLengths(event?.venues)
    ? await promptBookingSpotAndBringer(event, {
        comedianName: application.comedian_name,
        bookingHistoryRows,
      })
    : { spotCode: '', isBringer: false };
  if (spot == null) return { sent: false, cancelled: true };

  const draft = composeBookingOffer(application, event, emailTemplate, {
    spotCode: spot.spotCode,
    isBringer: spot.isBringer,
  });
  const edited = await promptEditBookingOffer(draft);
  if (!edited) return { sent: false, cancelled: true };

  const bookerEmail = await operatorEmail(supabase);
  const gmail = await invokeSendBookingOffer(supabase, { ...edited, cc: bookerEmail });

  if (gmail.kind === 'sent') {
    await persistApplicationOfferSpot(supabase, applicationId, spot, bookerEmail || null);
    if (bookerEmail) application.offered_by_email = bookerEmail;
    await recordOfferEmailSend(supabase, {
      applicationId,
      kind: 'offer',
      channel: 'gmail',
      toEmail: edited.to,
    });
    await onOffered(spot);
    const sheetNote = await syncSheetAfterOffer(supabase, application, event, spot);
    const who = gmail.deliveredTo || edited.to;
    const extra = gmail.redirected ? ' (dev redirect - comic was not emailed)' : '';
    const sheetExtra = sheetNote ? `\n\n${sheetNote}` : '';
    await Swal.fire(
      'Sent',
      `From sunsetcomedylondon@gmail.com to ${who}${extra}${sheetExtra}`,
      'success'
    );
    return { sent: true, channel: 'gmail' };
  }

  if (gmail.kind === 'error') {
    await Swal.fire('Email not sent', gmail.reason || 'Gmail send failed', 'error');
    return { sent: false };
  }

  openBookingMailtoLink(
    generateMailtoLink({
      email: edited.to,
      subject: edited.subject,
      body: edited.body,
    })
  );
  await persistApplicationOfferSpot(supabase, applicationId, spot, bookerEmail || null);
  if (bookerEmail) application.offered_by_email = bookerEmail;
  await recordOfferEmailSend(supabase, {
    applicationId,
    kind: 'offer',
    channel: 'mailto',
    toEmail: edited.to,
  });
  await onOffered(spot);
  await syncSheetAfterOffer(supabase, application, event, spot);
  return { sent: true, channel: 'mailto' };
}
